import {
  ApiInfoType,
  ApiResponse,
  DefaultApiDataInterface,
  IContactRespData,
  IDistCheckRequestPayload,
  IDistCheckRespData,
  IFieldRepRespData,
  IHttpError,
  ILogAutofillMessage,
  IServiceOrderRespData,
  StorePersonnelPayloadInterface,
  TNotFoundErrorList,
  TUseFetchResult,
} from "@lib/types";

let _API_HOST = "https://inventory-manager.mmonj.com";
let _API_TOKEN_KEY_NAME = "Ext-Inventory-Manager-token";

if (window.__IS_EXT_FS_UTIL_DEBUG__) {
  _API_HOST = "http://localhost:8001";
  _API_TOKEN_KEY_NAME = "Ext-Inventory-Manager-token-dev";
}

export const API_INFO: ApiInfoType = {
  api_host: _API_HOST,
  api_token: null,
  api_token_key_name: _API_TOKEN_KEY_NAME,
  get_dist_check_endpoint: `${_API_HOST}/api/get_store_product_additions/`,
  api_validation_endpoint: `${_API_HOST}/api/validate_api_token/`,
  get_service_order_info: `${_API_HOST}/api/get_service_order_info/`,
  // field_rep_info_endpoint: `${_API_HOST}/api/get_field_rep_info/`,
  get_field_reps_endpoint: `${_API_HOST}/api/get_field_reps/`,
  update_store_field_rep: `${_API_HOST}/api/update_store_field_rep/`,
  update_store_personnel: `${_API_HOST}/api/update_store_personnel/`,
  log_autofill_msg: `${_API_HOST}/survey_worker/api/log_autofill_message`,
};

export function getApiKeyFromStorage() {
  return localStorage.getItem(API_INFO.api_token_key_name);
}

export function setApiKeyToStorage(apiKey: string) {
  localStorage.setItem(API_INFO.api_token_key_name, apiKey);
  API_INFO.api_token = apiKey;
}

export function validateApiKey(token: string): Promise<ApiResponse<DefaultApiDataInterface>> {
  const headers = new Headers();
  headers.append("Authorization", `Token ${token}`);
  headers.append("Content-Type", "application/json");

  return fetch(API_INFO.api_validation_endpoint, {
    method: "GET",
    headers: headers,
    redirect: "follow",
  });
}

export function fetchJsonApi<T>(fetchCallback: () => Promise<ApiResponse<T>>): Promise<TUseFetchResult<T>> {
  return fetchCallback()
    .then((resp) => {
      if (!resp.ok) {
        // eslint-disable-next-line @typescript-eslint/no-throw-literal
        throw resp;
      }

      return resp.json();
    })
    .then((data: T) => {
      return { ok: true, data: data, errors: [] as string[] } as const;
    })
    .catch(async function (error: ApiResponse<IHttpError | TNotFoundErrorList> | Error) {
      const errorList = await getErrorList(error);
      return {
        ok: false,
        data: null,
        errors: errorList,
      } as const;
    });
}

export function getServiceOrderInfoFetcher(soid: number): () => Promise<ApiResponse<IServiceOrderRespData>> {
  const url = new URL(`${soid}/`, API_INFO.get_service_order_info);

  const headers = new Headers();
  headers.append("Authorization", `Token ${API_INFO.api_token!}`);
  headers.append("Content-Type", "application/json");

  return () =>
    fetch(url.toString(), {
      method: "GET",
      headers: headers,
      redirect: "follow",
    });
}

export function getFieldRepsFetcher(): () => Promise<ApiResponse<IFieldRepRespData[]>> {
  const headers = new Headers();
  headers.append("Authorization", `Token ${API_INFO.api_token!}`);
  headers.append("Content-Type", "application/json");

  return () =>
    fetch(API_INFO.get_field_reps_endpoint, {
      method: "GET",
      headers: headers,
      redirect: "follow",
    });
}

export function getFieldRepUpdateFetcher(
  storeId: number,
  newFieldRepId: number
): () => Promise<ApiResponse<IServiceOrderRespData>> {
  const headers = new Headers();
  headers.append("Authorization", `Token ${API_INFO.api_token!}`);
  headers.append("Content-Type", "application/json");

  return () =>
    fetch(API_INFO.update_store_field_rep, {
      method: "PUT",
      headers: headers,
      body: JSON.stringify({
        store_id: storeId,
        new_field_rep_id: newFieldRepId,
      }),
      redirect: "follow",
    });
}

export function getDistDataFetcher(
  apiPayload: IDistCheckRequestPayload
): () => Promise<ApiResponse<IDistCheckRespData>> {
  if (API_INFO.api_token == null) throw new Error("API token is not defined");

  const headers = new Headers();
  headers.append("Authorization", `Token ${API_INFO.api_token}`);
  headers.append("Content-Type", "application/json");

  return () =>
    fetch(API_INFO.get_dist_check_endpoint, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(apiPayload),
      redirect: "follow",
    });
}

export function getUpdateStorePersonnelFetcher(
  payload: StorePersonnelPayloadInterface
): () => Promise<ApiResponse<IContactRespData>> {
  const headers = new Headers();
  headers.append("Authorization", `Token ${API_INFO.api_token!}`);
  headers.append("Content-Type", "application/json");

  return () =>
    fetch(API_INFO.update_store_personnel, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(payload),
      redirect: "follow",
    });
}

export async function getErrorList(
  error: ApiResponse<TNotFoundErrorList | IHttpError> | Error
): Promise<string[]> {
  if (error instanceof Error) {
    return [error.message];
  }

  try {
    const jsonData = await error.json();

    if (Array.isArray(jsonData)) {
      return jsonData;
    } else if (typeof jsonData === "object" && "detail" in jsonData) {
      return [jsonData.detail];
    } else {
      return ["An unexpected error occurred"];
    }
  } catch {
    // when json error (most likely)
    return ["An unexpected error occurred"];
  }
}

export function logAutofillMessage(
  soid: number,
  messageType: ILogAutofillMessage["message_type"],
  message: string
) {
  const payload = {
    service_order_id: String(soid),
    message_type: messageType,
    message: message,
  } satisfies ILogAutofillMessage;

  console.log("Logging autofill message:", payload);

  return fetch(API_INFO.log_autofill_msg, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Token ${API_INFO.api_token!}`,
    },
    body: JSON.stringify(payload),
    redirect: "follow",
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to log autofill message: ${response.statusText}`);
      }

      console.log("Autofill message logged successfully");
      return response.json();
    })
    .then((data) => {
      console.log(`Server response from logging ${messageType}:`, data);
    })
    .catch((error) => {
      console.error("Error logging autofill message:", error);
    });
}
